import type { AppearanceBanner } from '@setplex/tria-api/src/entities/appearance'
import type { JSX } from 'react'
import type { ViewComponent } from '../../model'
import type { CarouselWithPrice } from '../cards/banner/index.h'

import { useStoreMap, useUnit } from 'effector-react'
import { cloneElement, memo, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import { bannerSlide, model as routerModel } from '~/core/router'
import { BannerRowContainerWithTrailerPortal } from '~/widgets/Banners/BannerRowContainerWithTrailerPortal'
import { loadContent as moduleLoadContent } from '../../model/components/model'
import { $viewComponents } from '../../model/view-components'
import { useBannerCarouselCards } from '../cards/banner/useBannerCarouselCards'
import { getCategoryId, getCollectionId, getOnlyFavorites } from '../lib/style'

const HOME_BANNER_TEST_ID = 'home-banners'

type BannersCarouselProps = { id: string; index: number }

export const Banner = memo(Banner_)

Banner_.displayName = 'Banner'
function Banner_({ index }: BannersCarouselProps): JSX.Element {
  const { ref: setRef, inView } = useInView()

  const componentData = useStoreMap({
    store: $viewComponents,
    keys: [index],
    fn: (loaded, [index]) => loaded[index],
  }) as ViewComponent<AppearanceBanner, CarouselWithPrice> | undefined

  const { appearance, type: componentState } = componentData ?? {}
  const bannerId = appearance?.id
  const locationKey = useUnit(routerModel.$key)

  const initialSlide =
    locationKey && bannerId != null
      ? bannerSlide.getBannerSlide(locationKey, String(bannerId))
      : undefined

  const { bannerBundles: content, autoScrollEnabled } =
    componentData?.content ?? {}

  const autoScrollIn = componentData?.content?.autoScrollIn

  const [loadContent] = useUnit([moduleLoadContent])

  const search = componentData?.appearance.resource.search
  const {
    chooseBannerCard,
    handleBannerSlideChanged,
    isBannerAutoScrollEnabled,
    isAnyPaymentPopupOpened,
    goToNextSlideTrigger,
  } = useBannerCarouselCards(
    {
      categoryId: search ? getCategoryId(search) : 0,
      collectionId: search ? getCollectionId(search) : '',
      onlyFavorites: search ? getOnlyFavorites(search) : false,
      categoryName: 'Banners',
    },
    {
      content,
      bannerId,
      autoScrollIn,
      autoScrollEnabled,
    }
  )

  const isLoadNeeded = componentState === 'Empty' || componentState === 'Dirty'

  const isBannersLoading =
    componentState === 'Loading' || componentState === 'Awaiting'

  useEffect(() => {
    if (appearance && inView && isLoadNeeded) {
      loadContent(appearance)
    }
  }, [loadContent, isLoadNeeded, appearance, inView])

  // Shows skeleton (isBannersLoading) or nothing ('Empty')
  const isSkeletonShown = isBannersLoading || componentState === 'Empty'

  const isNothingShown =
    componentState === 'Failed' || !content || content.length === 0
  const isSomethingShown = !isNothingShown

  if (isSkeletonShown || isSomethingShown) {
    return (
      <BannerRowContainerWithTrailerPortal
        setRef={setRef}
        infiniteLoop={true}
        loaded={!isSkeletonShown}
        childrenLength={content?.length ?? 0}
        autoScrollEnabled={isBannerAutoScrollEnabled}
        isPopupOpen={isAnyPaymentPopupOpened}
        autoScrollIn={autoScrollIn}
        onSlideChanged={handleBannerSlideChanged}
        initialSlide={initialSlide}
        goToNextSlideTrigger={goToNextSlideTrigger}
        dataTestIdBannersCarousel={HOME_BANNER_TEST_ID}
      >
        <>
          {isSkeletonShown && <></>}
          {isSomethingShown &&
            content.map((item) =>
              cloneElement(chooseBannerCard(item), {
                key: String(item.bannerBundleId),
              })
            )}
        </>
      </BannerRowContainerWithTrailerPortal>
    )
  }

  // isNothingShown
  return <></>
}
